import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { isAdminPauseReason } from '@/constants/pause'
import type { PauseDto } from '@/services/pause/interfaces/pause.interface'
import type { ISessionStatusActivePause } from '@/services/auth/interfaces/session-status-response.interface'

const STORAGE_PAUSED = 'operator_queue_paused'
const STORAGE_PAUSE_ID = 'operator_queue_pause_id'
const STORAGE_PAUSE_REASON = 'operator_queue_pause_reason'
const STORAGE_PAUSE_STARTED_AT = 'operator_queue_pause_started_at'
const STORAGE_PAUSE_BY_SUPERVISOR = 'operator_queue_pause_by_supervisor'

// Lo único que el store y el POST de pausa/despausa usan de una pausa de
// catálogo. Un PauseDto completo es asignable, y así una pausa reconstruida
// fuera del catálogo no necesita inventarse duration/status/created_at.
export type ActivePauseSelection = Pick<PauseDto, 'id' | 'name' | 'description'>

const pad = (value: number) => String(value).padStart(2, '0')

// Estado de pausa del agente, compartido entre AppHeader/AppSidebarMobile (la
// pausa que el agente elige del catálogo, con su pausa_id) y WebphoneDraggable
// (la que reporta Asterisk, sin pausa_id — solo el motivo con que se pausó, que
// es lo que distingue una pausa del supervisor de una del propio agente).
export const usePauseStatusStore = defineStore('pauseStatus', () => {
  const isPaused = ref(localStorage.getItem(STORAGE_PAUSED) === 'true')
  const selectedPause = ref<ActivePauseSelection | null>(null)
  const selectedPauseId = ref<string | null>(localStorage.getItem(STORAGE_PAUSE_ID))
  const pauseReasonLabel = ref<string | null>(localStorage.getItem(STORAGE_PAUSE_REASON))
  // Quién puso la pausa, como dato explícito. Antes se deducía de
  // `selectedPause === null`, y esa ausencia también ocurre con pausas propias
  // (recarga sin catálogo cargado, storage limpio, otro navegador, o el POST de
  // pausa que falló después de que Asterisk ya la aplicó): el agente quedaba
  // bloqueado con "solo un administrador puede reanudarla" sobre su propia pausa.
  const isSupervisorPause = ref(
    localStorage.getItem(STORAGE_PAUSE_BY_SUPERVISOR) === 'true',
  )
  // Cuándo cambió el estado por última vez en esta pestaña. Una respuesta del
  // backend pedida antes de ese momento ya no dice nada sobre el estado actual.
  let lastLocalChangeAt = 0

  const storedStartedAt = localStorage.getItem(STORAGE_PAUSE_STARTED_AT)
  const pauseStartedAt = ref<number | null>(storedStartedAt ? Number(storedStartedAt) : null)

  // Migración best-effort: si ya había una pausa activa en localStorage antes de
  // que existiera el cronómetro, arranca en 0 en vez de quedar en blanco para siempre.
  if (isPaused.value && !pauseStartedAt.value) {
    pauseStartedAt.value = Date.now()
    localStorage.setItem(STORAGE_PAUSE_STARTED_AT, String(pauseStartedAt.value))
  }

  const activePauseLabel = computed(
    () => selectedPause.value?.name || pauseReasonLabel.value || null,
  )

  // Un solo tick global (no uno por componente) para el cronómetro de la pausa —
  // arranca de una si ya estábamos en pausa al cargar la página (sobrevive a un reload).
  const now = ref(Date.now())
  let tickHandle: ReturnType<typeof setInterval> | null = null

  const startTicking = () => {
    if (tickHandle) return
    now.value = Date.now()
    tickHandle = setInterval(() => {
      now.value = Date.now()
    }, 1000)
  }

  const stopTicking = () => {
    if (tickHandle) {
      clearInterval(tickHandle)
      tickHandle = null
    }
  }

  if (isPaused.value) startTicking()

  const pauseElapsedSeconds = computed(() => {
    if (!isPaused.value || !pauseStartedAt.value) return 0
    return Math.max(0, Math.floor((now.value - pauseStartedAt.value) / 1000))
  })

  const pauseElapsedLabel = computed(() => {
    if (!isPaused.value || !pauseStartedAt.value) return ''

    const totalSeconds = pauseElapsedSeconds.value
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    return hours > 0
      ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(minutes)}:${pad(seconds)}`
  })

  const formattedPauseSummary = computed(() => {
    if (!isPaused.value) return ''
    const reason = activePauseLabel.value || 'Pausa'
    const time = pauseElapsedLabel.value || '00:00'
    return `En pausa · ${reason} · ${time}`
  })

  const setPauseStartedAt = (startedAt: number) => {
    pauseStartedAt.value = startedAt
    localStorage.setItem(STORAGE_PAUSE_STARTED_AT, String(startedAt))
  }

  const applySelfPause = (pause: ActivePauseSelection, startedAt?: number) => {
    lastLocalChangeAt = Date.now()
    isPaused.value = true
    selectedPause.value = pause
    selectedPauseId.value = pause.id
    pauseReasonLabel.value = pause.name
    isSupervisorPause.value = false
    localStorage.setItem(STORAGE_PAUSED, 'true')
    localStorage.setItem(STORAGE_PAUSE_ID, pause.id)
    localStorage.setItem(STORAGE_PAUSE_REASON, pause.name)
    localStorage.setItem(STORAGE_PAUSE_BY_SUPERVISOR, 'false')
    setPauseStartedAt(startedAt ?? Date.now())
    startTicking()
  }

  const clearPause = () => {
    lastLocalChangeAt = Date.now()
    isPaused.value = false
    selectedPause.value = null
    selectedPauseId.value = null
    pauseReasonLabel.value = null
    pauseStartedAt.value = null
    isSupervisorPause.value = false
    localStorage.setItem(STORAGE_PAUSED, 'false')
    localStorage.removeItem(STORAGE_PAUSE_ID)
    localStorage.removeItem(STORAGE_PAUSE_REASON)
    localStorage.removeItem(STORAGE_PAUSE_STARTED_AT)
    localStorage.removeItem(STORAGE_PAUSE_BY_SUPERVISOR)
    stopTicking()
  }

  // Pausa que reporta Asterisk y el store no tiene registrada: pasa al arrancar
  // sin localStorage, al entrar desde otro navegador, o cuando la respuesta del
  // POST de pausa no volvió. Quién la puso lo dice el propio motivo — el monitor
  // pausa con ADMIN_PAUSE_REASON y el agente con el nombre de su pausa de
  // catálogo —, no la ausencia de datos locales: asumir supervisor por descarte
  // es lo que dejaba al agente bloqueado en su propia pausa.
  //
  // startedAt permite reconstruir el cronómetro con la hora real de Asterisk
  // (LastPause de QueueMemberGrouped) en vez de "ahora", importante cuando nos
  // enteramos tarde (reload, o el próximo poll de 3s).
  const applyPauseFromAsterisk = (reason: string | undefined, startedAt?: number) => {
    const bySupervisor = isAdminPauseReason(reason)
    const label = reason || ''

    // Ya sabíamos de la pausa: se reconcilia solo el origen, sin tocar el
    // pause_id de catálogo ni reiniciar el cronómetro. Cubre al supervisor que
    // pausa a un agente que ya estaba en su propia pausa — Asterisk tiene un
    // único estado de pausa, así que el motivo pasa a ser el del monitor.
    if (isPaused.value) {
      if (bySupervisor === isSupervisorPause.value) return

      isSupervisorPause.value = bySupervisor
      localStorage.setItem(STORAGE_PAUSE_BY_SUPERVISOR, String(bySupervisor))

      if (bySupervisor) {
        selectedPause.value = null
        selectedPauseId.value = null
        pauseReasonLabel.value = label
        localStorage.removeItem(STORAGE_PAUSE_ID)
        localStorage.setItem(STORAGE_PAUSE_REASON, label)
      }
      return
    }

    lastLocalChangeAt = Date.now()
    isPaused.value = true
    selectedPause.value = null
    selectedPauseId.value = null
    pauseReasonLabel.value = label
    isSupervisorPause.value = bySupervisor
    localStorage.setItem(STORAGE_PAUSED, 'true')
    localStorage.removeItem(STORAGE_PAUSE_ID)
    localStorage.setItem(STORAGE_PAUSE_REASON, label)
    localStorage.setItem(STORAGE_PAUSE_BY_SUPERVISOR, String(bySupervisor))
    setPauseStartedAt(startedAt ?? Date.now())
    startTicking()
  }

  // La pausa abierta en pause_logs, que trae session-status. El estado guardado
  // en localStorage es del navegador, y el heartbeat de Asterisk que lo corrige
  // solo llega a quien es miembro de alguna cola: sin esto, un agente de solo
  // chat o sin registro en campañas quedaba "pausado" para siempre en ese
  // navegador, o al revés, sin ver una pausa que sí estaba abierta.
  //
  // requestedAt es cuándo salió la consulta: si el agente pausó o reanudó
  // después, la respuesta es vieja y no se aplica.
  const syncWithBackend = (
    activePause: ISessionStatusActivePause | null | undefined,
    requestedAt: number,
  ) => {
    if (activePause === undefined) return
    if (lastLocalChangeAt > requestedAt) return

    if (!activePause) {
      if (isPaused.value) clearPause()
      return
    }

    if (isPaused.value) return
    const startedAt = Date.parse(activePause.started_at)
    applyPauseFromAsterisk(
      activePause.name ?? undefined,
      Number.isFinite(startedAt) ? startedAt : undefined,
    )
  }

  // Resuelve selectedPause contra el catálogo cargado — al recargar la página solo
  // persiste el id, no el objeto completo.
  const hydrateFromCatalog = (pauses: PauseDto[]) => {
    if (!selectedPauseId.value || selectedPause.value) return

    const match = pauses.find((pause) => pause.id === selectedPauseId.value)
    if (match) {
      selectedPause.value = match
      pauseReasonLabel.value = match.name
    }
  }

  return {
    isPaused,
    selectedPause,
    selectedPauseId,
    pauseReasonLabel,
    pauseStartedAt,
    activePauseLabel,
    formattedPauseSummary,
    pauseElapsedSeconds,
    pauseElapsedLabel,
    isSupervisorPause,
    applySelfPause,
    applyPauseFromAsterisk,
    clearPause,
    syncWithBackend,
    hydrateFromCatalog,
  }
})